/**
 * ROOFTOPS family — mesh side of the realism pass. Turns the pure piece list
 * from `rooftopsLayout.ts` into ONE merged mesh per material (one draw call per material, ≤ 9
 * per map: 9 materials max), built once at load. No lights, no per-frame work: the aviation
 * beacons are emissive + bloom. Shared by `rooftops.ts`, `rooftops_xl.ts` and
 * `rooftop_spiderweb.ts` (this file exports no `mapIds`, so the registry
 * skips it).
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapExtrasContext } from "../mapExtras.js";
import { surfaceMaps, uvAnchor, worldProjectUV } from "../mapTextures.js";
import { rooftopsLayout, type RoofMat, type RoofPieceX } from "./rooftopsLayout.js";
import { skylineLayout } from "./skylineRooftops.js";

const tmpC = new THREE.Color();
function setColor(geo: THREE.BufferGeometry, hex: number, mul: number): void {
  tmpC.setHex(hex);
  const n = geo.getAttribute("position").count;
  const c = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { c[i * 3] = tmpC.r * mul; c[i * 3 + 1] = tmpC.g * mul; c[i * 3 + 2] = tmpC.b * mul; }
  geo.setAttribute("color", new THREE.BufferAttribute(c, 3));
}

function pieceGeo(p: RoofPieceX): THREE.BufferGeometry {
  let g: THREE.BufferGeometry;
  if (p.shape === "cyl") {
    // cross diameter = the larger of the two cross extents; length = axis extent
    const len = p.axis === "x" ? p.sx : p.axis === "z" ? p.sz : p.sy;
    const dia = p.axis === "x" ? Math.max(p.sy, p.sz) : p.axis === "z" ? Math.max(p.sx, p.sy) : Math.max(p.sx, p.sz);
    const r = dia / 2;
    const rTop = p.taper >= 1 ? r : r * p.taper, rBot = p.taper >= 1 ? r / p.taper : r;
    const seg = dia > 0.5 ? 20 : dia > 0.15 ? 12 : 6;
    g = new THREE.CylinderGeometry(rTop, rBot, Math.max(len, 0.004), seg, 1, false);
    if (p.axis === "x") g.rotateZ(-Math.PI / 2);      // +Y → +X
    else if (p.axis === "z") g.rotateX(Math.PI / 2);  // +Y → +Z
  } else {
    g = new THREE.BoxGeometry(Math.max(p.sx, 0.004), Math.max(p.sy, 0.004), Math.max(p.sz, 0.004));
  }
  // world-anchored UVs (tile per family) so merged pieces share one pattern
  const tile = p.mat === "roof" ? 3.4 : p.mat === "paver" ? 1.2 : p.mat === "louvre" ? 2.0 : p.mat === "facade" ? 4.5 : p.mat === "tank" ? 2.2 : 1.6;
  // the facade keeps BOX-LOCAL UVs like the map's own buildings (its window
  // grid is laid out relative to the box); everything else is world-anchored
  if (p.mat === "facade") worldProjectUV(g, tile);
  else worldProjectUV(g, tile, uvAnchor(p.x, tile), uvAnchor(p.y, tile), uvAnchor(p.z, tile));
  g.translate(p.x, p.y, p.z);
  setColor(g, p.color, p.mat === "beacon" ? 3 : 1);
  return g.index ? g.toNonIndexed() : g;
}

const MATS: Record<RoofMat, () => THREE.Material> = {
  metal: () => new THREE.MeshStandardMaterial(surfaceMaps("galv", { color: 0xffffff, vertexColors: true, roughness: 0.55, metalness: 0.5 }, 0.6)),
  louvre: () => new THREE.MeshStandardMaterial(surfaceMaps("louvre", { color: 0xffffff, vertexColors: true, roughness: 0.7, metalness: 0.3 })),
  roof: () => new THREE.MeshStandardMaterial(surfaceMaps("roofing", {
    color: 0xffffff, vertexColors: true, roughness: 0.92, metalness: 0,
    polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
  })),
  paver: () => new THREE.MeshStandardMaterial(surfaceMaps("concrete", { color: 0xffffff, vertexColors: true, roughness: 0.9, metalness: 0 })),
  dark: () => new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.75, metalness: 0.35 }),
  glass: () => new THREE.MeshStandardMaterial({ color: 0x9fb8c6, roughness: 0.06, metalness: 0.65, envMapIntensity: 1.4 }),
  beacon: () => new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
  facade: () => new THREE.MeshStandardMaterial(surfaceMaps("facade", { color: 0xffffff, vertexColors: true, roughness: 0.6, metalness: 0.15 })),
  tank: () => new THREE.MeshStandardMaterial(surfaceMaps("wood", { color: 0xffffff, vertexColors: true, roughness: 0.9, metalness: 0 })),
};

export function buildRooftopExtras(ctx: MapExtrasContext): THREE.Object3D | null {
  // the spiderweb keeps decor.ts's synthwave skyline; the day maps get a city
  const pieces = rooftopsLayout(ctx.map, ctx.lowSpec);
  if (ctx.map.id !== "rooftop_spiderweb") pieces.push(...skylineLayout(ctx.map, ctx.lowSpec));
  if (!pieces.length) return null;
  const groups = new Map<RoofMat, THREE.BufferGeometry[]>();
  for (const p of pieces) {
    const g = pieceGeo(p);
    (groups.get(p.mat) ?? groups.set(p.mat, []).get(p.mat)!).push(g);
  }
  const root = new THREE.Group();
  root.name = `extras:${ctx.map.id}`;
  for (const [mat, geos] of groups) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[rooftops extras] merge failed for ${mat}`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, MATS[mat]());
    mesh.name = `rt:${mat}`;
    const solid = mat === "metal" || mat === "louvre";
    // the far city casts no shadow (out of the shadow camera anyway)
    mesh.castShadow = ctx.shadows && solid;
    mesh.receiveShadow = mat !== "beacon";
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    root.add(mesh);
  }
  return root;
}
